import { useEffect, useRef, useState } from "react";
import { getRoleDocuments } from "../../auth/apiLanguage";
import { DOCUMENT_STATUS, canonicalRoleId, hasDocumentContent, statusFromError } from "../../../utils/roleDocuments";

const emptyDocuments = { plan: [], term: [], agree: [] };

// Per-document status: a role can have its Business Plan but no Final
// Agreement yet -- each tab says so on its own instead of one global "No data".
const documentStatuses = (documents) => ({
  plan: hasDocumentContent(documents.plan) ? DOCUMENT_STATUS.SUCCESS : DOCUMENT_STATUS.EMPTY,
  term: hasDocumentContent(documents.term) ? DOCUMENT_STATUS.SUCCESS : DOCUMENT_STATUS.EMPTY,
  agree: hasDocumentContent(documents.agree) ? DOCUMENT_STATUS.SUCCESS : DOCUMENT_STATUS.EMPTY,
});

const uniformStatuses = (status) => ({ plan: status, term: status, agree: status });

/**
 * Loads the read access (readable roles, invited role, expiry) and the three
 * documents of the viewed role from GET /language/role-documents.
 *
 * - `invitationToken` is passed explicitly (URL token first) -- never read
 *   from localStorage here.
 * - Everything is reset whenever the token, the signed-in account or the
 *   language changes, and stale responses are ignored (request sequencing).
 * - Reading never changes the account's role or the invitation.
 */
const useRoleDocuments = ({ invitationToken = null, eventToken = null, authToken, lang = "en", viewedRole = null, enabled = true, loadAccess = true, refreshKey = 0 }) => {
  const [access, setAccess] = useState(null);
  const [accessStatus, setAccessStatus] = useState(enabled && loadAccess ? DOCUMENT_STATUS.LOADING : DOCUMENT_STATUS.IDLE);
  const [documentsState, setDocumentsState] = useState({
    role: null,
    documents: emptyDocuments,
    statuses: uniformStatuses(DOCUMENT_STATUS.IDLE),
  });
  const accessSeq = useRef(0);
  const docsSeq = useRef(0);
  const [retryCount, setRetryCount] = useState(0);

  useEffect(() => {
    if (!enabled || !loadAccess) {
      setAccessStatus(DOCUMENT_STATUS.IDLE);
      return undefined;
    }
    const seq = ++accessSeq.current;
    const controller = typeof AbortController !== "undefined" ? new AbortController() : null;
    setAccess(null);
    setAccessStatus(DOCUMENT_STATUS.LOADING);
    getRoleDocuments({ invitationToken, eventToken, authToken, lang, signal: controller?.signal }).then((result) => {
      if (seq !== accessSeq.current || result.aborted) return;
      if (result.ok) {
        setAccess(result.data?.access || null);
        setAccessStatus(DOCUMENT_STATUS.SUCCESS);
      } else {
        setAccessStatus(statusFromError(result.error));
      }
    });
    return () => controller?.abort();
  }, [enabled, loadAccess, invitationToken, eventToken, authToken, lang, refreshKey, retryCount]);

  const role = canonicalRoleId(viewedRole);

  useEffect(() => {
    if (!enabled || !role) return undefined;
    const seq = ++docsSeq.current;
    const controller = typeof AbortController !== "undefined" ? new AbortController() : null;
    setDocumentsState({ role, documents: emptyDocuments, statuses: uniformStatuses(DOCUMENT_STATUS.LOADING) });
    getRoleDocuments({ role, invitationToken, eventToken, authToken, lang, signal: controller?.signal }).then((result) => {
      if (seq !== docsSeq.current || result.aborted) return;
      if (result.ok) {
        const documents = { ...emptyDocuments, ...(result.data?.documents || {}) };
        setDocumentsState({ role, documents, statuses: documentStatuses(documents), agreementVersion: result.data?.agreementVersion, language: result.data?.language });
        if (result.data?.access) setAccess(result.data.access);
      } else {
        setDocumentsState({ role, documents: emptyDocuments, statuses: uniformStatuses(statusFromError(result.error)), error: result.error });
        if (result.error?.code === "RECIPIENT_MISMATCH") setAccess((prev) => prev && { ...prev, invitationStatus: "recipient_mismatch" });
      }
    });
    return () => controller?.abort();
  }, [enabled, role, invitationToken, eventToken, authToken, lang, refreshKey, retryCount]);

  return {
    access,
    accessStatus,
    documents: documentsState.documents,
    statuses: documentsState.role === role ? documentsState.statuses : uniformStatuses(role ? DOCUMENT_STATUS.LOADING : DOCUMENT_STATUS.IDLE),
    error: documentsState.error || null,
    agreementVersion: documentsState.agreementVersion,
    language: documentsState.language,
    retry: () => setRetryCount((count) => count + 1),
  };
};

export default useRoleDocuments;
